import {
  MultiParentTransformationsExistingLineItem,
  MultiParentTransformationsNewLineItem,
  MultiParentTransformationsRuleInputs,
  Rule,
} from "@rebuild/shared";
import { IDatabaseLineItem } from "../../pages/CreatorTools/LineItemDBCreationTool/types";
import { MultiParentTransformationsRuleInputPaths, resolveArrayPath, ValidationError, ValidationErrorEntry } from "./types";

type MultiParentTransformationsRule = Rule & { templateId: "multi_parent_transformations" };
type ValidationResult = { path: string; validationError: ValidationError };

// --- Existing line item param checks ---

function checkExistingLineItemMissing(param: MultiParentTransformationsExistingLineItem, index: number): ValidationResult | undefined {
  if (!param.lineItem?.lineItemId) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("existingLineItemParams[number].lineItem", index),
      validationError: ValidationError.MissingLineItemInExistingParam,
    };
  }
}

function checkExistingLineItemInvalid(
  param: MultiParentTransformationsExistingLineItem,
  index: number,
  dbLineItems: IDatabaseLineItem[],
): ValidationResult | undefined {
  const id = param.lineItem?.lineItemId;
  if (id && !dbLineItems.some((li) => li.id === id)) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("existingLineItemParams[number].lineItem", index),
      validationError: ValidationError.InvalidExistingLineItem,
    };
  }
}

function checkIfExistingLineItemQuantityFunctionIsMissing(
  param: MultiParentTransformationsExistingLineItem,
  index: number,
): ValidationResult | undefined {
  if (!!param.shouldKeep && param.quantityFunction === undefined) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("existingLineItemParams[number].quantityFunction", index),
      validationError: ValidationError.InvalidQuantityFunction,
    };
  }
}

function checkExistingLineItemInheritQuantityValue(
  param: MultiParentTransformationsExistingLineItem,
  index: number,
  existingParamCount: number,
): ValidationResult | undefined {
  if (param.quantityFunction !== "INHERIT_FROM_SPECIFIC_EXISTING") return undefined;
  if (!param.quantityValue) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("existingLineItemParams[number].quantityValue", index),
      validationError: ValidationError.MissingQuantityValueForInherit,
    };
  }
  const parsed = Number(param.quantityValue);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > existingParamCount) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("existingLineItemParams[number].quantityValue", index),
      validationError: ValidationError.InvalidQuantityValueForInherit,
    };
  }
}

function checkExistingLineItemDefaultQuantityValue(
  param: MultiParentTransformationsExistingLineItem,
  index: number,
): ValidationResult | undefined {
  if (param.quantityFunction === "USE_DEFAULT" && !param.quantityValue) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("existingLineItemParams[number].quantityValue", index),
      validationError: ValidationError.MissingQuantityValueForDefault,
    };
  }
}

// --- New line item param checks ---

function checkNewLineItemMissing(param: MultiParentTransformationsNewLineItem, index: number): ValidationResult | undefined {
  if (!param.lineItem?.lineItemId) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("newLineItemParams[number].lineItem", index),
      validationError: ValidationError.MissingLineItemInNewParam,
    };
  }
}

function checkNewLineItemInvalid(
  param: MultiParentTransformationsNewLineItem,
  index: number,
  dbLineItems: IDatabaseLineItem[],
): ValidationResult | undefined {
  const id = param.lineItem?.lineItemId;
  if (id && !dbLineItems.some((li) => li.id === id)) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("newLineItemParams[number].lineItem", index),
      validationError: ValidationError.InvalidNewLineItem,
    };
  }
}

function checkIfNewLineItemQuantityFunctionIsMissing(
  param: MultiParentTransformationsNewLineItem,
  index: number,
): ValidationResult | undefined {
  if (param.quantityFunction === undefined) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("newLineItemParams[number].quantityFunction", index),
      validationError: ValidationError.InvalidQuantityFunction,
    };
  }
}

function checkNewLineItemDefaultQuantityValue(param: MultiParentTransformationsNewLineItem, index: number): ValidationResult | undefined {
  if (param.quantityFunction === "USE_DEFAULT" && !param.quantityValue) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("newLineItemParams[number].quantityValue", index),
      validationError: ValidationError.MissingQuantityValueForDefault,
    };
  }
}

function checkNewLineItemInheritQuantityValue(
  param: MultiParentTransformationsNewLineItem,
  index: number,
  existingParamCount: number,
): ValidationResult | undefined {
  if (param.quantityFunction !== "INHERIT_FROM_SPECIFIC_EXISTING") return undefined;
  if (!param.quantityValue) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("newLineItemParams[number].quantityValue", index),
      validationError: ValidationError.MissingQuantityValueForInherit,
    };
  }
  const parsed = Number(param.quantityValue);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > existingParamCount) {
    return {
      path: resolveArrayPath<MultiParentTransformationsRuleInputs>("newLineItemParams[number].quantityValue", index),
      validationError: ValidationError.InvalidQuantityValueForInherit,
    };
  }
}

export function validateMultiParentTransformations(
  rule: MultiParentTransformationsRule,
  dbLineItems: IDatabaseLineItem[],
): Map<MultiParentTransformationsRuleInputPaths, ValidationErrorEntry> | undefined {
  const existingParamCount = (rule.ruleInputs.existingLineItemParams ?? []).length;
  const results: ValidationResult[] = [
    ...(rule.ruleInputs.existingLineItemParams ?? []).flatMap((param, i) => [
      checkExistingLineItemMissing(param, i),
      checkExistingLineItemInvalid(param, i, dbLineItems),
      checkIfExistingLineItemQuantityFunctionIsMissing(param, i),
      checkExistingLineItemInheritQuantityValue(param, i, existingParamCount),
      checkExistingLineItemDefaultQuantityValue(param, i),
    ]),
    ...(rule.ruleInputs.newLineItemParams ?? []).flatMap((param, i) => [
      checkNewLineItemMissing(param, i),
      checkNewLineItemInvalid(param, i, dbLineItems),
      checkIfNewLineItemQuantityFunctionIsMissing(param, i),
      checkNewLineItemInheritQuantityValue(param, i, existingParamCount),
      checkNewLineItemDefaultQuantityValue(param, i),
    ]),
  ].filter((r): r is ValidationResult => r !== undefined);

  if (results.length === 0) return undefined;

  return new Map(
    results.map(({ path, validationError }) => [
      path as MultiParentTransformationsRuleInputPaths,
      { validationError } satisfies ValidationErrorEntry,
    ]),
  );
}
